/**
 * Auth0 SPA authentication using `@auth0/auth0-spa-js`.
 *
 * Replaces the old Cognito + `amazon-cognito-identity-js` module. Auth0
 * handles the OAuth 2.1 authorization-code + PKCE flow via Universal Login;
 * this module is a thin wrapper that initializes the SDK and exposes
 * token-getting for the API calls.
 */

import { Auth0Client } from '@auth0/auth0-spa-js';
import type { AppConfig } from '../config';

let client: Auth0Client | null = null;

export type TokenBundle = {
  accessToken: string;
  expiresAt: number;
  email: string;
};

export function initAuth0(config: AppConfig): Auth0Client {
  if (client) return client;
  client = new Auth0Client({
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/callback`,
      audience: config.auth0Audience,
      scope: 'openid profile email',
    },
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
  });
  return client;
}

export function getAuth0(): Auth0Client {
  if (!client) throw new Error('Auth0 not initialized — call initAuth0 first');
  return client;
}

export async function signIn(opts?: { screenHint?: 'signup' }): Promise<void> {
  await getAuth0().loginWithRedirect(
    opts?.screenHint ? { authorizationParams: { screen_hint: opts.screenHint } } : undefined,
  );
}

export async function handleRedirectCallback(): Promise<void> {
  await getAuth0().handleRedirectCallback();
}

export async function signOut(): Promise<void> {
  await getAuth0().logout({ logoutParams: { returnTo: window.location.origin } });
}

export async function getCachedSession(): Promise<TokenBundle | null> {
  const auth0 = getAuth0();
  const authenticated = await auth0.isAuthenticated();
  if (!authenticated) return null;
  try {
    const token = await auth0.getTokenSilently();
    const user = await auth0.getUser();
    return {
      accessToken: token,
      expiresAt: Date.now() + 3600_000, // approx; the SDK refreshes internally
      email: user?.email ?? user?.nickname ?? 'unknown',
    };
  } catch {
    return null;
  }
}

export async function getValidAccessToken(): Promise<string | null> {
  const auth0 = getAuth0();
  const authenticated = await auth0.isAuthenticated();
  if (!authenticated) return null;
  try {
    return await auth0.getTokenSilently();
  } catch {
    return null;
  }
}